import { useEffect, useMemo, useRef, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { List, Map as MapIcon, Plus, Search, X } from 'lucide-react';
import { z } from 'zod';
import type { ProjectResponse } from '@/api/api';
import { AppShell } from '@/components/shared/AppShell';
import { AuroraBackground } from '@/components/shared/AuroraBackground';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { meOptions } from '@/features/auth/api';
import { projectsListOptions, useMyProjects } from '@/features/projects/api';
import { CreateProjectDialog } from '@/features/projects/components/CreateProjectDialog';
import { ProjectListRow } from '@/features/projects/components/ProjectListRow';
import { CityMap } from '@/features/projects/components/city/CityMap';
import { MAX_ZOOM, MIN_ZOOM, planCity } from '@/features/projects/components/city/geometry';

const projectsSearchSchema = z.object({
  // Map is the default; the list stays one click away for long portfolios.
  view: z.enum(['map', 'list']).optional(),
  q: z.string().optional(),
  // Map zoom relative to fit-to-screen; omitted means 1 (fit).
  zoom: z.number().min(MIN_ZOOM).max(MAX_ZOOM).optional().catch(undefined),
});

export const Route = createFileRoute('/_authed/projects/')({
  validateSearch: projectsSearchSchema,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectsListOptions()),
    ]),
  component: ProjectPickerPage,
});

function matchesQuery(p: ProjectResponse, q: string) {
  return p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false);
}

function ProjectPickerPage() {
  const { data: me } = useSuspenseQuery(meOptions());
  const { view = 'map', q: rawQuery = '', zoom = 1 } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [createOpen, setCreateOpen] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { active: visible, mine, isPending: membersResolving } = useMyProjects(me.id);

  const query = rawQuery.trim().toLowerCase();
  const filtered = useMemo(
    () => (query === '' ? mine : mine.filter((p) => matchesQuery(p, query))),
    [mine, query],
  );
  const matches = useMemo(
    () => (query === '' ? null : new Set(filtered.map((p) => p.id))),
    [filtered, query],
  );

  // The plan follows the full roster, not the search, so lots never move while filtering.
  const lotCount = membersResolving ? visible.length : mine.length;
  const plan = useMemo(() => planCity(lotCount), [lotCount]);
  const codeById = useMemo(() => {
    const codes = new Map<string, string>();
    for (const b of plan.blocks) {
      const project = b.kind === 'lot' ? mine[b.projectIndex] : undefined;
      if (project) codes.set(project.id, b.code);
    }
    return codes;
  }, [plan, mine]);

  const setSearch = (next: { view?: 'map' | 'list'; q?: string; zoom?: number }) =>
    void navigate({
      search: (prev) => {
        const merged = { ...prev, ...next };
        return {
          view: merged.view === 'list' ? 'list' : undefined,
          q: merged.q ? merged.q : undefined,
          zoom: merged.zoom !== undefined && merged.zoom !== 1 ? merged.zoom : undefined,
        };
      },
      replace: true,
    });

  // "/" jumps to search from anywhere on the page, unless the user is already typing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const empty = !membersResolving && mine.length === 0;

  return (
    <AppShell currentUser={{ id: me.id, displayName: me.display_name, email: me.email }}>
      <div className="relative isolate flex h-full flex-col lg:block">
        <AuroraBackground contained />
        <aside
          className="z-10 m-4 flex shrink-0 flex-col gap-4 rounded-lg border border-city-panel-edge bg-city-panel p-4 text-city-panel-ink shadow-lg lg:absolute lg:left-0 lg:top-0 lg:w-80"
          aria-label="Projects controls"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h1 className="flex items-baseline gap-2 text-2xl font-medium tracking-tight">
                Projects
                {!membersResolving && mine.length > 0 ? (
                  <span className="text-base font-normal tabular-nums text-city-panel-ink-muted">
                    {mine.length}
                  </span>
                ) : null}
              </h1>
              <p className="text-sm text-city-panel-ink-muted">
                {membersResolving
                  ? 'Finding the projects you belong to…'
                  : empty
                    ? 'No projects yet.'
                    : 'Pick a project to open its timeline.'}
              </p>
            </div>
            {!empty ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="shrink-0"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                New project
              </Button>
            ) : null}
          </div>

          {empty ? (
            <div className="space-y-3">
              <p className="text-sm text-city-panel-ink-muted">
                Create one to get started, or ask an administrator to add you to an existing
                project.
              </p>
              <Button type="button" variant="primary" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Create project
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-city-panel-ink-muted"
                    aria-hidden="true"
                  />
                  <Input
                    ref={searchRef}
                    aria-label="Search projects"
                    placeholder="Search projects"
                    value={rawQuery}
                    disabled={membersResolving}
                    onChange={(e) => setSearch({ q: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && rawQuery !== '') {
                        e.preventDefault();
                        setSearch({ q: '' });
                      }
                    }}
                    className="border-city-panel-line bg-city-panel-field pl-8 pr-9 text-city-panel-ink shadow-none placeholder:text-city-panel-ink-muted"
                  />
                  {rawQuery !== '' ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch({ q: '' });
                        searchRef.current?.focus();
                      }}
                      className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-city-panel-ink-muted transition-colors hover:bg-city-panel hover:text-city-panel-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : (
                    <kbd
                      className="pointer-events-none absolute right-2 top-1/2 flex h-5 min-w-5 -translate-y-1/2 items-center justify-center rounded-md border border-city-panel-line bg-city-panel px-1.5 font-sans text-xs text-city-panel-ink-muted"
                      aria-hidden="true"
                    >
                      /
                    </kbd>
                  )}
                </div>
                <div
                  className="flex shrink-0 rounded-md border border-city-panel-line bg-city-panel-field p-0.5"
                  role="group"
                  aria-label="View"
                >
                  {(
                    [
                      { id: 'map', label: 'Map', Icon: MapIcon },
                      { id: 'list', label: 'List', Icon: List },
                    ] as const
                  ).map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      aria-pressed={view === id}
                      aria-label={`${label} view`}
                      title={`${label} view`}
                      onClick={() => setSearch({ view: id })}
                      className={cn(
                        'flex h-7 w-8 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        view === id
                          ? 'bg-city-panel-ink text-city-panel'
                          : 'text-city-panel-ink-muted hover:text-city-panel-ink',
                      )}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>

              {view === 'map' && query !== '' ? (
                filtered.length === 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm">No projects match &ldquo;{rawQuery.trim()}&rdquo;.</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSearch({ q: '' })}
                    >
                      Clear search
                    </Button>
                  </div>
                ) : (
                  <ul className="-mx-2 space-y-0.5" aria-label="Matching projects">
                    {filtered.slice(0, 6).map((p) => (
                      <li key={p.id}>
                        <Link
                          to="/projects/$projectId/timeline"
                          params={{ projectId: p.id }}
                          onPointerEnter={() => setHighlightId(p.id)}
                          onPointerLeave={() => setHighlightId(null)}
                          onFocus={() => setHighlightId(p.id)}
                          onBlur={() => setHighlightId(null)}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-city-panel-field focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <span className="shrink-0 rounded-md bg-city-panel-ink px-1.5 text-xs font-medium tabular-nums text-city-panel">
                            {codeById.get(p.id)}
                          </span>
                          <span className="truncate">{p.name}</span>
                        </Link>
                      </li>
                    ))}
                    {filtered.length > 6 ? (
                      <li className="px-2 pt-1 text-xs tabular-nums text-city-panel-ink-muted">
                        {filtered.length - 6} more lit on the map
                      </li>
                    ) : null}
                  </ul>
                )
              ) : view === 'map' ? (
                <p className="text-xs text-city-panel-ink-muted">Taller buildings have bigger teams.</p>
              ) : null}
            </>
          )}
        </aside>

        {view === 'list' && !empty ? (
          <div className="relative min-h-0 flex-1 overflow-y-auto lg:h-full">
            <div className="px-6 pb-12 pt-2 lg:pl-96 lg:pr-8 lg:pt-6 2xl:px-6">
              <div className="mx-auto max-w-3xl space-y-3">
                {membersResolving ? (
                  <ul className="overflow-hidden rounded-lg border border-city-panel-edge bg-city-panel shadow-lg">
                    {visible.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center gap-4 border-t border-city-panel-line px-4 py-3 first:border-t-0"
                      >
                        <span className="h-9 w-11 shrink-0 animate-pulse rounded-md bg-city-panel-field" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3.5 w-48 animate-pulse rounded-md bg-city-panel-field" />
                          <div className="h-3 w-80 animate-pulse rounded-md bg-city-panel-field" />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-city-panel-edge bg-city-panel px-6 py-10 text-center text-city-panel-ink">
                    <p className="text-sm">No projects match &ldquo;{rawQuery.trim()}&rdquo;</p>
                    <p className="text-xs text-city-panel-ink-muted">
                      Search looks at project names and descriptions.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSearch({ q: '' })}
                    >
                      Clear search
                    </Button>
                  </div>
                ) : (
                  <>
                    <ul className="overflow-hidden rounded-lg border border-city-panel-edge bg-city-panel shadow-lg">
                      {filtered.map((project) => (
                        <ProjectListRow
                          key={project.id}
                          project={project}
                          currentUserId={me.id}
                          lotCode={codeById.get(project.id)}
                        />
                      ))}
                    </ul>
                    {query !== '' ? (
                      <p
                        className="text-xs tabular-nums text-city-table-ink-muted"
                        aria-live="polite"
                      >
                        {filtered.length} of {mine.length} projects
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative min-h-0 flex-1 lg:h-full">
            <CityMap
              plan={plan}
              projects={membersResolving ? [] : mine}
              currentUserId={me.id}
              matches={matches}
              highlightId={highlightId}
              zoom={zoom}
              onZoomChange={(next) => setSearch({ zoom: next })}
            />
          </div>
        )}
      </div>
      <CreateProjectDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </AppShell>
  );
}
